import { getApiBaseUrlAsync, initializeApiUrl } from '../config/apiConfig';
import i18n from '../i18n';

const AUTH_HEADER_PREFIX = 'Bearer ';

// Re-export for backward compatibility
export { getApiBaseUrlAsync as getApiBaseUrl };

// Cache for API URL (initialized once)
let cachedApiBaseUrl = null;
let apiUrlInitialized = false;

// ==================== REQUEST QUEUE/POOL SYSTEM ====================
const MAX_CONCURRENT_REQUESTS = 5;
const requestQueue = [];
let activeRequests = 0;

const processQueue = () => {
  if (activeRequests >= MAX_CONCURRENT_REQUESTS || requestQueue.length === 0) {
    return;
  }

  const { resolve, reject, requestFn } = requestQueue.shift();
  activeRequests++;

  requestFn()
    .then((result) => {
      activeRequests--;
      resolve(result);
      processQueue(); // Process next request in queue
    })
    .catch((error) => {
      activeRequests--;
      reject(error);
      processQueue(); // Process next request in queue
    });
};

const queueRequest = (requestFn) => {
  return new Promise((resolve, reject) => {
    requestQueue.push({ resolve, reject, requestFn });
    processQueue();
  });
};

// ==================== ERROR LOG THROTTLING ====================
const errorLogCache = new Map(); // { errorKey: lastLoggedTime }
const ERROR_LOG_THROTTLE_MS = 10000; // 10 seconds

const shouldLogError = (errorKey) => {
  const now = Date.now();
  const lastLogged = errorLogCache.get(errorKey);
  
  if (!lastLogged || (now - lastLogged) > ERROR_LOG_THROTTLE_MS) {
    errorLogCache.set(errorKey, now);
    return true;
  }
  
  return false;
};

// ==================== INITIALIZATION ====================
const ensureApiUrlInitialized = async () => {
  if (apiUrlInitialized && cachedApiBaseUrl) {
    return cachedApiBaseUrl;
  }
  
  try {
    await initializeApiUrl();
    cachedApiBaseUrl = await getApiBaseUrlAsync();
    apiUrlInitialized = true;
    return cachedApiBaseUrl;
  } catch (error) {
    console.error('❌ [cloudApi] Failed to initialize API URL:', error);
    const errorMsg = error.message || i18n.t('shared:cloudApiErrors.unknownError');
    throw new Error(i18n.t('shared:cloudApiErrors.apiUrlInitFailed', { message: errorMsg }));
  }
};

const buildUrl = async (path) => {
  const apiBaseUrl = await ensureApiUrlInitialized();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const fullUrl = `${apiBaseUrl}${normalizedPath}`;
  
  return fullUrl;
};

// ==================== JSON PARSING ====================
const parseJsonSafely = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  
  const text = await response.text();
  if (!text) {
    return null;
  }
  
  // If content-type is not JSON (e.g., HTML error page from Cloudflare/nginx)
  if (!isJson) {
    // Check if it's an HTML error page
    if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
      const status = response.status;
      const statusText = response.statusText;
      
      let errorMessage = i18n.t('shared:cloudApiErrors.serverErrorStatus', { status });
      if (status === 502) {
        errorMessage = i18n.t('shared:cloudApiErrors.serverError502');
      } else if (status === 500) {
        errorMessage = i18n.t('shared:cloudApiErrors.serverError500');
      } else if (status === 503) {
        errorMessage = i18n.t('shared:cloudApiErrors.serverError503');
      }
      
      const errorKey = `html_error_${status}_${response.url}`;
      if (shouldLogError(errorKey)) {
        console.warn('⚠️ HTML error response received:', {
          status,
          statusText,
          contentType,
          preview: text.substring(0, 200)
        });
      }
      
      return {
        ok: false,
        error: `HTTP_${status}`,
        message: errorMessage,
        isHtmlError: true
      };
    }
    
    const errorKey = `non_json_${response.url}`;
    if (shouldLogError(errorKey)) {
      console.warn('⚠️ Non-JSON response received:', { contentType, preview: text.substring(0, 200) });
    }
  }
  
  try {
    return JSON.parse(text);
  } catch (error) {
    const errorKey = `json_parse_${response.url}`;
    if (shouldLogError(errorKey)) {
      console.warn('API yanıtı JSON parse edilemedi:', error, text.substring(0, 200));
    }
    
    if (!isJson) {
      return {
        ok: false,
        error: 'INVALID_RESPONSE_FORMAT',
        message: i18n.t('shared:cloudApiErrors.invalidResponseFormat'),
        rawText: text.substring(0, 500)
      };
    }
    
    return null;
  }
};

// ==================== ERROR MAPPING ====================
const mapFetchError = (error, url = null) => {
  if (error.name === 'AbortError') {
    const abortError = new Error(i18n.t('shared:cloudApiErrors.requestCancelled'));
    if (url) abortError.url = url;
    return abortError;
  }

  // Check for DNS/network errors - treat as OFFLINE
  const isNetworkError = 
    error.message?.includes('Failed to fetch') ||
    error.message?.includes('ERR_NAME_NOT_RESOLVED') ||
    error.message?.includes('ERR_CONNECTION_REFUSED') ||
    error.message?.includes('NetworkError') ||
    error.message?.includes('ERR_INSUFFICIENT_RESOURCES') ||
    error.code === 'ENOTFOUND' ||
    error.code === 'ECONNREFUSED' ||
    error.code === 'NETWORK_UNREACHABLE';

  if (isNetworkError) {
    const urlInfo = url || error.url || error.config?.url || i18n.t('shared:cloudApiErrors.unknownUrl');
    const networkError = new Error(i18n.t('shared:cloudApiErrors.cannotConnectToServer'));
    networkError.code = 'NETWORK_UNREACHABLE';
    networkError.url = urlInfo;

    // Notify the app that the network is unreachable so UI can react immediately
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('notesplus-network-error', {
        detail: { url: urlInfo }
      }));
    }

    return networkError;
  }

  if (url && !error.url) {
    error.url = url;
  }

  return error;
};

// ==================== REQUEST TIMEOUT & RETRY ====================
const DEFAULT_REQUEST_TIMEOUT = 10 * 1000; // 10 seconds
const MAX_RETRIES = 2;
const RETRY_DELAY = 2 * 1000; // 2 seconds

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * One logical HTTP request: queue once, then retry inside the same concurrency slot.
 * Retries must NOT call queueRequest again (that caused a 5-slot deadlock).
 */
const requestWithRetry = async (path, options = {}) => {
  const { method = 'GET', token, body, headers, signal, timeout = DEFAULT_REQUEST_TIMEOUT, skipQueue = false } = options;

  const runRequest = async () => {
    // attempt 0..MAX_RETRIES inclusive → same total tries as the old nested requestWithRetry
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      const fullUrl = await buildUrl(path);

      // Fresh AbortController per attempt so a prior timeout abort cannot poison retries
      const controller = !signal && typeof AbortController !== 'undefined' ? new AbortController() : null;
      const requestSignal = signal || controller?.signal || undefined;

      let timeoutId = null;
      let isTimeout = false;
      if (timeout > 0 && controller) {
        timeoutId = setTimeout(() => {
          isTimeout = true;
          controller.abort();
        }, timeout);
      }

      const mergedHeaders = {
        Accept: 'application/json',
        ...headers,
      };

      if (token) {
        mergedHeaders.Authorization = `${AUTH_HEADER_PREFIX}${token}`;
      } else if (process.env.NODE_ENV === 'production' && path !== '/auth/login' && path !== '/auth/register') {
        const errorKey = `no_token_${path}`;
        if (shouldLogError(errorKey)) {
          console.warn('⚠️ [cloudApi] Request made without token:', { path, method });
        }
      }

      let requestBody = body;
      if (body && !(body instanceof FormData)) {
        mergedHeaders['Content-Type'] = 'application/json';
        requestBody = JSON.stringify(body);
      }

      try {
        const response = await fetch(fullUrl, {
          method,
          headers: mergedHeaders,
          body: requestBody,
          signal: requestSignal,
        });

        const payload = await parseJsonSafely(response);

        if (!response.ok) {
          // Handle server errors (500-599)
          if (response.status >= 500 && response.status < 600) {
            let errorMessage = payload?.message || i18n.t('shared:cloudApiErrors.serverErrorRetry', { status: response.status });

            if (response.status === 502) {
              errorMessage = i18n.t('shared:cloudApiErrors.cannotConnect502');
            } else if (response.status === 404) {
              errorMessage = i18n.t('shared:cloudApiErrors.cannotConnect404');
            }

            const errorKey = `server_error_${response.status}_${fullUrl}`;
            if (shouldLogError(errorKey)) {
              console.error('❌ [cloudApi] Server error:', {
                status: response.status,
                statusText: response.statusText,
                url: fullUrl,
                path,
                method,
              });
            }

            const serverError = new Error(errorMessage);
            serverError.status = response.status;
            serverError.code = 'SERVER_ERROR';
            serverError.url = fullUrl;
            if (payload?.isHtmlError) {
              serverError.isHtmlError = true;
              serverError.payload = payload;
            }

            throw serverError;
          }

          // Handle HTML error pages (for non-500 errors)
          if (payload?.isHtmlError) {
            const error = new Error(payload.message || i18n.t('shared:cloudApiErrors.serverErrorStatus', { status: response.status }));
            error.status = response.status;
            error.payload = payload;
            error.isHtmlError = true;
            throw error;
          }

          // Handle structured JSON errors (400-499)
          // 401: oturum sona ermiş — global event fırlat (AuthContext yakalar)
          if (response.status === 401 && typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('notesplus-unauthorized'));
          }
          const error = new Error(payload?.message || payload?.error || response.statusText);
          error.status = response.status;
          error.payload = payload;
          throw error;
        }

        // If response is ok but payload indicates an error
        if (payload && payload.ok === false && payload.isHtmlError) {
          const error = new Error(payload.message || i18n.t('shared:cloudApiErrors.genericServerError'));
          error.status = response.status || 500;
          error.payload = payload;
          error.isHtmlError = true;
          throw error;
        }

        return payload;
      } catch (unknownError) {
        const error = mapFetchError(unknownError, fullUrl);

        if (!error.url) {
          error.url = fullUrl;
        }

        const isTimeoutError = isTimeout || (error.name === 'AbortError' && timeoutId && isTimeout);
        const isNetworkError =
          error.code === 'NETWORK_UNREACHABLE' || error.code === 'LOCAL_SERVER_NOT_RUNNING';

        // Timeout — retry inside this same queue slot (do not re-enter queueRequest)
        if (isTimeoutError) {
          const errorKey = `timeout_${fullUrl}`;
          if (shouldLogError(errorKey)) {
            console.error('⏱️ [cloudApi] Request timeout:', fullUrl);
          }

          if (attempt < MAX_RETRIES) {
            await sleep(RETRY_DELAY);
            continue;
          }

          const timeoutError = new Error(i18n.t('shared:cloudApiErrors.requestTimedOut', { url: fullUrl }));
          timeoutError.code = 'TIMEOUT';
          timeoutError.name = 'TimeoutError';
          timeoutError.url = fullUrl;
          timeoutError.silent = true;
          throw timeoutError;
        }

        // Network — retry inside this same queue slot
        if (isNetworkError) {
          const errorKey = `network_error_${fullUrl}`;
          if (shouldLogError(errorKey)) {
            console.error('❌ [cloudApi] Network error:', {
              url: fullUrl,
              path,
              error: error.message,
              code: error.code,
            });
          }

          if (attempt < MAX_RETRIES) {
            await sleep(RETRY_DELAY);
            continue;
          }

          throw error;
        }

        // Server errors (502, 500, etc.) — no retry (same as before)
        if (error.code === 'SERVER_ERROR') {
          const errorKey = `server_error_${fullUrl}`;
          if (shouldLogError(errorKey)) {
            console.error('❌ [cloudApi] Server error:', {
              url: fullUrl,
              path,
              status: error.status,
              error: error.message,
            });
          }
          throw error;
        }

        // 404 MESSAGE_NOT_FOUND for event-participants is expected (e.g. deleted message) - skip log
        const isEventParticipants404 =
          error.status === 404 &&
          (error.payload?.error === 'MESSAGE_NOT_FOUND' || path.includes('event-participants'));
        if (!isEventParticipants404) {
          const errorKey = `request_failed_${fullUrl}`;
          if (shouldLogError(errorKey)) {
            console.error('❌ [cloudApi] Request failed:', {
              url: fullUrl,
              path,
              error: error.message,
              status: error.status,
            });
          }
        }
        throw error;
      } finally {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (controller) {
          controller.abort();
        }
      }
    }

    // Unreachable: loop always returns or throws
    throw new Error(i18n.t('shared:cloudApiErrors.genericServerError'));
  };

  // Queue once per logical request; retries stay inside runRequest
  if (skipQueue) {
    return runRequest();
  }

  return queueRequest(runRequest);
};

// ==================== PUBLIC REQUEST FUNCTION ====================
export const request = async (path, options = {}) => {
  return requestWithRetry(path, options);
};

// ==================== API EXPORTS ====================
export const authApi = {
  async requestRegisterCode({ email, name }) {
    return request('/auth/register/request-code', {
      method: 'POST',
      body: { email, name, locale: i18n.language },
      timeout: 30 * 1000, // 30 seconds - e-posta gönderme işlemi uzun sürebilir
    });
  },

  async register({ email, password, name, code }) {
    return request('/auth/register', {
      method: 'POST',
      body: { email, password, name, code },
    });
  },

  async login({ email, password }) {
    return request('/auth/login', {
      method: 'POST',
      body: { email, password },
    });
  },

  async searchUserByEmail({ email, token }) {
    return request(`/users/search-by-email?email=${encodeURIComponent(email)}`, {
      method: 'GET',
      token,
    });
  },

  async searchUsers({ query, token }) {
    return request(`/users/search?q=${encodeURIComponent(query)}`, {
      method: 'GET',
      token,
    });
  },

  async updateProfile({ token, name, avatarUrl }) {
    return request('/me', {
      method: 'PUT',
      token,
      body: {
        name,
        avatarUrl,
      },
    });
  },

  async updateStatus({ token, current_section }) {
    return request('/me', {
      method: 'POST',
      token,
      body: {
        current_section,
      },
    });
  },

  async confirmNewUserFromGoogle({ googleData }) {
    return request('/auth/oauth/confirm-new-user', {
      method: 'POST',
      body: {
        googleData,
      },
    });
  },

  async requestPasswordResetCode({ email }) {
    return request('/auth/password/reset/request-code', {
      method: 'POST',
      body: { email, locale: i18n.language },
      timeout: 30 * 1000, // 30 seconds - e-posta gönderme işlemi uzun sürebilir
    });
  },

  async resetPassword({ email, password, code }) {
    return request('/auth/password/reset/confirm', {
      method: 'POST',
      body: { email, password, code },
    });
  },

  async getProviderStartUrl(provider, platform = 'web', parentOrigin = null) {
    const params = new URLSearchParams({ platform, locale: i18n.language });
    if (parentOrigin) {
      params.set('parentOrigin', parentOrigin);
    }
    return request(`/auth/providers/${provider}/start?${params.toString()}`, {
      method: 'GET',
    });
  },
};

export const notesApi = {
  async list({ token, since }) {
    const searchParams = new URLSearchParams();
    if (since) {
      searchParams.set('since', since);
    }
    const query = searchParams.toString();
    return request(`/notes${query ? `?${query}` : ''}`, {
      method: 'GET',
      token,
    });
  },

  async upsert({ token, notes }) {
    return request('/notes', {
      method: 'POST',
      token,
      body: Array.isArray(notes) ? notes : [notes],
    });
  },

  async update({ token, noteId, note }) {
    const bodySize = note?.content ? String(note.content).length : 0;
    const timeout = bodySize > 50000 ? 60000 : 30000;
    return request(`/notes/${noteId}`, {
      method: 'PUT',
      token,
      body: note,
      timeout,
    });
  },

  async delete({ token, noteId }) {
    return request(`/notes/${noteId}`, {
      method: 'DELETE',
      token,
    });
  },

  async deleteAll({ token }) {
    return request('/notes', {
      method: 'DELETE',
      token,
    });
  },

  async getOrder({ token }) {
    return request('/notes/order', {
      method: 'GET',
      token,
    });
  },

  async reorder({ token, noteIds }) {
    return request('/notes/reorder', {
      method: 'PATCH',
      token,
      body: { noteIds: Array.isArray(noteIds) ? noteIds : [] },
    });
  },

  async share({ token, noteId, receiverIds, groupId }) {
    return request('/notes/share', {
      method: 'POST',
      token,
      body: {
        noteId,
        receiverIds: receiverIds || [],
        groupId: groupId || null,
      },
    });
  },

  async saveShared({ token, messageId, groupId }) {
    return request('/notes/save-shared', {
      method: 'POST',
      token,
      body: {
        messageId,
        groupId: groupId || null,
      },
    });
  },

  // Birlikte Çalış (Plus only)
  async collabInvite({ token, noteId, receiverIds, groupId }) {
    return request('/notes/collab-invite', {
      method: 'POST',
      token,
      body: {
        noteId,
        receiverIds: receiverIds || [],
        groupId: groupId || null,
      },
    });
  },
  async collabInviteRespond({ token, messageId, groupId, accept }) {
    return request('/notes/collab-invite/respond', {
      method: 'POST',
      token,
      body: { messageId, groupId: groupId || null, accept: !!accept },
    });
  },
  async leaveCollaborativeNote({ token, noteId }) {
    return request('/notes/leave', {
      method: 'POST',
      token,
      body: { noteId },
    });
  },
  async transferNoteOwnership({ token, noteId, newOwnerUserId }) {
    return request('/notes/transfer-ownership', {
      method: 'POST',
      token,
      body: { noteId, newOwnerUserId },
    });
  },
  async removeCollaborator({ token, noteId, userId: editorUserId }) {
    return request('/notes/remove-collaborator', {
      method: 'POST',
      token,
      body: { noteId, userId: editorUserId },
    });
  },
  async freezeCollaborator({ token, noteId, userId }) {
    return request('/notes/freeze-collaborator', {
      method: 'POST',
      token,
      body: { noteId, userId },
    });
  },
};

export const calendarsApi = {
  async list({ token, since }) {
    const searchParams = new URLSearchParams();
    if (since) {
      searchParams.set('since', since);
    }
    const query = searchParams.toString();
    return request(`/calendars${query ? `?${query}` : ''}`, {
      method: 'GET',
      token,
    });
  },

  async upsert({ token, calendars }) {
    return request('/calendars', {
      method: 'POST',
      token,
      body: calendars,
    });
  },

  async update({ token, calendarId, calendar }) {
    return request(`/calendars/${calendarId}`, {
      method: 'PUT',
      token,
      body: calendar,
    });
  },

  async delete({ token, calendarId }) {
    return request(`/calendars/${calendarId}`, {
      method: 'DELETE',
      token,
    });
  },

  async deleteAll({ token }) {
    return request('/calendars', {
      method: 'DELETE',
      token,
    });
  },
};

export const eventsApi = {
  async list({ token, since }) {
    const searchParams = new URLSearchParams();
    if (since) {
      searchParams.set('since', since);
    }
    const query = searchParams.toString();
    return request(`/events${query ? `?${query}` : ''}`, {
      method: 'GET',
      token,
    });
  },

  async upsert({ token, events }) {
    return request('/events', {
      method: 'POST',
      token,
      body: events,
    });
  },

  async update({ token, eventId, event }) {
    return request(`/events/${eventId}`, {
      method: 'PUT',
      token,
      body: event,
    });
  },

  async delete({ token, eventId }) {
    return request(`/events/${eventId}`, {
      method: 'DELETE',
      token,
    });
  },

  async deleteAll({ token }) {
    return request('/events', {
      method: 'DELETE',
      token,
    });
  },

  async deleteByGroup({ token, groupId }) {
    return request(`/events/by-group/${groupId}`, {
      method: 'DELETE',
      token,
    });
  },
};

export const breaksApi = {
  async list({ token, since }) {
    const searchParams = new URLSearchParams();
    if (since) {
      searchParams.set('since', since);
    }
    const query = searchParams.toString();
    return request(`/breaks${query ? `?${query}` : ''}`, {
      method: 'GET',
      token,
    });
  },

  async upsert({ token, breaks }) {
    return request('/breaks', {
      method: 'POST',
      token,
      body: breaks,
    });
  },

  async update({ token, breakId, breakData }) {
    return request(`/breaks/${breakId}`, {
      method: 'PUT',
      token,
      body: breakData,
    });
  },

  async delete({ token, breakId }) {
    return request(`/breaks/${breakId}`, {
      method: 'DELETE',
      token,
    });
  },

  async deleteAll({ token }) {
    return request('/breaks', {
      method: 'DELETE',
      token,
    });
  },
};

export const chatApi = {
  async getFriends({ token }) {
    return request('/chat/friends', {
      method: 'GET',
      token,
    });
  },

  async pinFriend({ token, friendId, pinned }) {
    return request(`/chat/friends/${friendId}/pin`, {
      method: 'POST',
      token,
      body: { pinned },
    });
  },

  async muteFriend({ token, friendId, mutedUntil }) {
    return request(`/chat/friends/${friendId}/mute`, {
      method: 'POST',
      token,
      body: { mutedUntil },
    });
  },

  async setFriendNickname({ token, friendId, nickname }) {
    return request(`/chat/friends/${friendId}/nickname`, {
      method: 'PUT',
      token,
      body: { nickname },
    });
  },

  async deleteFriend({ token, friendId }) {
    return request(`/chat/friends/${friendId}`, {
      method: 'DELETE',
      token,
    });
  },

  async getMessages({ token, friendId, since, signal }) {
    const searchParams = new URLSearchParams();
    if (since) {
      searchParams.set('since', since);
    }
    const query = searchParams.toString();
    return request(`/chat/messages/${friendId}${query ? `?${query}` : ''}`, {
      method: 'GET',
      token,
      signal,
    });
  },

  async sendMessage({ token, receiverId, content, type = 'text', fileUrl = null, fileName = null, replyTo = null, forwardedFrom = null, event_id = null, event_title = null, event_start = null, event_end = null, event_all_day = false, event_description = null, event_location = null, poll_title = null, poll_options = null, poll_multiple_choice = false, poll_secret = false, contact_user_id = null, contact_name = null, contact_avatar_url = null, contact_email = null }) {
    return request('/chat/messages', {
      method: 'POST',
      token,
      body: { receiverId, content, type, fileUrl, fileName, replyTo, forwardedFrom, event_id, event_title, event_start, event_end, event_all_day, event_description, event_location, poll_title, poll_options, poll_multiple_choice, poll_secret, contact_user_id, contact_name, contact_avatar_url, contact_email },
    });
  },

  /** POST /chat/messages/:messageId/event-response - Event participation (attending | not_attending | maybe) for DM. Optional calendar_id when multiple calendars. */
  async sendEventResponse({ token, messageId, status, calendar_id }) {
    return request(`/chat/messages/${messageId}/event-response`, {
      method: 'POST',
      token,
      body: { status, ...(calendar_id ? { calendar_id } : {}) },
    });
  },

  /** GET /chat/messages/:messageId/event-participants - Get participants for event message (DM) */
  async getEventParticipants({ token, messageId }) {
    return request(`/chat/messages/${messageId}/event-participants`, {
      method: 'GET',
      token,
    });
  },

  /** GET /chat/polls/:pollId - Get poll with options and vote counts */
  async getPoll({ token, pollId }) {
    return request(`/chat/polls/${pollId}`, {
      method: 'GET',
      token,
    });
  },

  /** POST /chat/polls/:pollId/vote - Vote on poll (option_id or option_ids for multiple_choice) */
  async votePoll({ token, pollId, option_id, option_ids }) {
    return request(`/chat/polls/${pollId}/vote`, {
      method: 'POST',
      token,
      body: { option_id, option_ids },
    });
  },

  /** GET /chat/polls/:pollId/votes-detail - Full list who voted for which option (only when !is_secret) */
  async getPollVotesDetail({ token, pollId }) {
    return request(`/chat/polls/${pollId}/votes-detail`, {
      method: 'GET',
      token,
    });
  },

  async getUnreadCount({ token }) {
    return request('/chat/unread-count', {
      method: 'GET',
      token,
    });
  },

  async deleteMessages({ token, friendId }) {
    return request(`/chat/messages/${friendId}`, {
      method: 'DELETE',
      token,
    });
  },

  async deleteMessage({ token, messageId, deleteType = 'for_me' }) {
    return request(`/chat/message/${messageId}`, {
      method: 'DELETE',
      token,
      body: { deleteType },
    });
  },

  async reportMessage({ token, messageId, reason, description }) {
    return request(`/chat/messages/${messageId}/report`, {
      method: 'POST',
      token,
      body: {
        reason,
        ...(description != null && description !== '' ? { description } : {}),
      },
    });
  },

  async deleteGroupMessage({ token, groupId, messageId, deleteType = 'for_me' }) {
    return request(`/chat/groups/${groupId}/message/${messageId}`, {
      method: 'DELETE',
      token,
      body: { deleteType },
    });
  },

  async reactToMessage({ token, messageId, emoji }) {
    return request(`/chat/messages/${messageId}/react`, {
      method: 'POST',
      token,
      body: { emoji },
    });
  },

  // Friend Request System (Mail-based)
  async sendFriendRequest({ token, receiverEmail }) {
    return request('/chat/friend-requests', {
      method: 'POST',
      token,
      body: { receiverEmail },
    });
  },

  async getIncomingRequests({ token }) {
    return request('/chat/friend-requests/incoming', {
      method: 'GET',
      token,
    });
  },

  async getOutgoingRequests({ token }) {
    return request('/chat/friend-requests/outgoing', {
      method: 'GET',
      token,
    });
  },

  async acceptFriendRequest({ token, requestId }) {
    return request(`/chat/friend-requests/${requestId}/accept`, {
      method: 'POST',
      token,
    });
  },

  async rejectFriendRequest({ token, requestId }) {
    return request(`/chat/friend-requests/${requestId}/reject`, {
      method: 'POST',
      token,
    });
  },

  async cancelFriendRequest({ token, requestId }) {
    return request(`/chat/friend-requests/${requestId}/cancel`, {
      method: 'POST',
      token,
    });
  },
};

export const groupApi = {
  // Grup CRUD
  async createGroup({ token, name, description, avatarUrl, memberEmails }) {
    return request('/chat/groups', {
      method: 'POST',
      token,
      body: { name, description, avatarUrl, memberEmails },
    });
  },

  async getGroups({ token }) {
    return request(`/chat/groups?locale=${encodeURIComponent(i18n.language)}`, {
      method: 'GET',
      token,
    });
  },

  async getGroup({ token, groupId }) {
    return request(`/chat/groups/${groupId}`, {
      method: 'GET',
      token,
    });
  },

  async updateGroup({ token, groupId, name, description, avatarUrl }) {
    return request(`/chat/groups/${groupId}`, {
      method: 'PUT',
      token,
      body: { name, description, avatarUrl },
    });
  },

  async deleteGroup({ token, groupId }) {
    return request(`/chat/groups/${groupId}`, {
      method: 'DELETE',
      token,
    });
  },

  // Üye yönetimi
  async addMember({ token, groupId, memberEmail }) {
    return request(`/chat/groups/${groupId}/members`, {
      method: 'POST',
      token,
      body: { memberEmail },
    });
  },

  async removeMember({ token, groupId, memberId }) {
    return request(`/chat/groups/${groupId}/members/${memberId}`, {
      method: 'DELETE',
      token,
    });
  },

  // Promote member to admin (Kurucu yetkisi)
  async promoteMember({ token, groupId, memberId }) {
    return request(`/chat/groups/${groupId}/members/${memberId}/promote`, {
      method: 'POST',
      token,
    });
  },

  // Demote admin to member (Kurucu yetkisi)
  async demoteMember({ token, groupId, memberId }) {
    return request(`/chat/groups/${groupId}/members/${memberId}/demote`, {
      method: 'POST',
      token,
    });
  },

  async leaveGroup({ token, groupId }) {
    return request(`/chat/groups/${groupId}/leave`, {
      method: 'POST',
      token,
    });
  },

  async muteGroup({ token, groupId, muted }) {
    return request(`/chat/groups/${groupId}/mute`, {
      method: 'POST',
      token,
      body: { muted },
    });
  },

  // Mesajlar
  async getGroupMessages({ token, groupId, since }) {
    const searchParams = new URLSearchParams();
    if (since) {
      searchParams.set('since', since);
    }
    const query = searchParams.toString();
    return request(`/chat/groups/${groupId}/messages${query ? `?${query}` : ''}`, {
      method: 'GET',
      token,
    });
  },

  async sendGroupMessage({ token, groupId, content, type = 'text', fileUrl = null, fileName = null, replyTo = null, forwardedFrom = null, mentionedUserIds = [], event_id = null, event_title = null, event_start = null, event_end = null, event_all_day = false, event_description = null, event_location = null, poll_title = null, poll_options = null, poll_multiple_choice = false, poll_secret = false, contact_user_id = null, contact_name = null, contact_avatar_url = null, contact_email = null }) {
    return request(`/chat/groups/${groupId}/messages`, {
      method: 'POST',
      token,
      body: { content, type, fileUrl, fileName, replyTo, forwardedFrom, mentionedUserIds, event_id, event_title, event_start, event_end, event_all_day, event_description, event_location, poll_title, poll_options, poll_multiple_choice, poll_secret, contact_user_id, contact_name, contact_avatar_url, contact_email },
    });
  },

  /** POST /chat/groups/:groupId/messages/:messageId/event-response - Event participation for group. Optional calendar_id when multiple calendars. */
  async sendGroupEventResponse({ token, groupId, messageId, status, calendar_id }) {
    return request(`/chat/groups/${groupId}/messages/${messageId}/event-response`, {
      method: 'POST',
      token,
      body: { status, ...(calendar_id ? { calendar_id } : {}) },
    });
  },

  /** GET /chat/groups/:groupId/messages/:messageId/event-participants - Get participants for event message (group) */
  async getGroupEventParticipants({ token, groupId, messageId }) {
    return request(`/chat/groups/${groupId}/messages/${messageId}/event-participants`, {
      method: 'GET',
      token,
    });
  },

  async markGroupMessageAsRead({ token, groupId, messageId }) {
    return request(`/chat/groups/${groupId}/messages/${messageId}/read`, {
      method: 'POST',
      token,
    });
  },

  // Destroy group (owner only)
  async destroyGroup({ token, groupId }) {
    return request(`/chat/groups/${groupId}/destroy`, {
      method: 'POST',
      token,
    });
  },

  // Clear messages for current user (personal)
  async clearGroupMessages({ token, groupId }) {
    return request(`/chat/groups/${groupId}/messages/clear`, {
      method: 'POST',
      token,
    });
  },

  // Delete all messages (hard delete, owner only)
  async deleteGroupMessages({ token, groupId }) {
    return request(`/chat/groups/${groupId}/messages`, {
      method: 'DELETE',
      token,
    });
  },

  // Hide group from sidebar (user-specific, persistent)
  async hideGroup({ token, groupId }) {
    return request(`/chat/groups/${groupId}/hide`, {
      method: 'POST',
      token,
    });
  },

  async pinGroup({ token, groupId, pinned }) {
    return request(`/chat/groups/${groupId}/pin`, {
      method: 'POST',
      token,
      body: { pinned },
    });
  },

  // Star/Unstar a group message
  async starGroupMessage({ token, groupId, messageId, starred }) {
    return request(`/chat/groups/${groupId}/messages/${messageId}/star`, {
      method: 'POST',
      token,
      body: { starred },
    });
  },

  // Get starred messages for a group
  async getStarredGroupMessages({ token, groupId }) {
    return request(`/chat/groups/${groupId}/messages/starred`, {
      method: 'GET',
      token,
    });
  },
};

export const notificationApi = {
  async getNotifications({ token }) {
    return request('/notifications', {
      method: 'GET',
      token,
    });
  },

  async markAsRead({ token, notificationId }) {
    return request(`/notifications/${notificationId}/read`, {
      method: 'POST',
      token,
    });
  },

  async markAllAsRead({ token }) {
    return request('/notifications/read-all', {
      method: 'POST',
      token,
    });
  },

  async getUnreadCount({ token }) {
    return request('/notifications/unread-count', {
      method: 'GET',
      token,
    });
  },

  async deleteNotification({ token, notificationId }) {
    return request(`/notifications/${notificationId}`, {
      method: 'DELETE',
      token,
    });
  },
};

export const licenseApi = {
  // Validate license/Plus status
  async validate({ token }) {
    return request('/license/validate', {
      method: 'GET',
      token,
    });
  },

  // Activate license
  async activate({ token, licenseKey, hwid }) {
    return request('/license/activate', {
      method: 'POST',
      token,
      body: { licenseKey, hwid },
    });
  },
};

export const subscriptionsApi = {
  // Kullanıcının mevcut Plus/abonelik durumunu getir (tek merkezi kaynak)
  async getMe({ token }) {
    return request('/subscriptions/me', {
      method: 'GET',
      token,
    });
  },

  // Microsoft Store IAP — satın alım sonrası Plus aktifleştirme
  async storeActivate({ token, storeId, purchaseStatus }) {
    // Legacy — server returns 410; kept so old builds fail clearly
    return request('/subscriptions/store-activate', {
      method: 'POST',
      token,
      body: { storeId, purchaseStatus },
    });
  },

  async getStoreCollectionsTicket({ token }) {
    return request('/subscriptions/store-collections-ticket', {
      method: 'GET',
      token,
    });
  },

  async storeVerify({ token, userStoreId, force = false }) {
    return request('/subscriptions/store-verify', {
      method: 'POST',
      token,
      body: { userStoreId, force: force === true },
    });
  },
};

// Avatar upload/remove — unified /avatars endpoints (multipart, never base64)
export const avatarApi = {
  async upload({ token, file, targetType = 'user', groupId = null }) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('targetType', targetType);
    if (groupId) formData.append('groupId', groupId);
    return request('/avatars/upload', {
      method: 'POST',
      token,
      body: formData,
    });
  },

  async remove({ token }) {
    return request('/avatars/user', {
      method: 'DELETE',
      token,
    });
  },

  async uploadGroup({ token, groupId, file }) {
    return avatarApi.upload({ token, file, targetType: 'group', groupId });
  },

  async removeGroup({ token, groupId }) {
    return request(`/avatars/group/${groupId}`, {
      method: 'DELETE',
      token,
    });
  },
};

export const userApi = {
  // Onboarding'i tamamlandı olarak işaretle
  async completeOnboarding({ token }) {
    return request('/me/onboarding', {
      method: 'PATCH',
      token,
      body: { onboarding_completed: true },
    });
  },

  async changePassword({ token, currentPassword, newPassword }) {
    return request('/me/password', {
      method: 'PUT',
      token,
      body: { currentPassword, newPassword },
    });
  },

  async deleteAccount({ token, password }) {
    return request('/me', {
      method: 'DELETE',
      token,
      body: password != null ? { password } : {},
    });
  },

  // Get user's PIN status
  async getPin({ token }) {
    return request('/me/pin', {
      method: 'GET',
      token,
    });
  },

  // Set (first time) or change PIN — pass currentPin when changing
  async setPin({ token, pin, currentPin }) {
    const body = { pin };
    if (currentPin != null) body.currentPin = currentPin;
    return request('/me/pin', {
      method: 'POST',
      token,
      body,
    });
  },

  // Remove PIN (currentPin required)
  async removePin({ token, currentPin }) {
    return request('/me/pin', {
      method: 'DELETE',
      token,
      body: { currentPin },
    });
  },

  // Verify user's PIN
  async verifyPin({ token, pin }) {
    return request('/me/pin/verify', {
      method: 'POST',
      token,
      body: { pin },
    });
  },

  // Forgot PIN — request email OTP (auth required; uses account email)
  async requestPinResetCode({ token, locale }) {
    return request('/me/pin/forgot/request-code', {
      method: 'POST',
      token,
      body: { locale },
    });
  },

  // Forgot PIN — confirm OTP + new PIN
  async confirmPinReset({ token, code, newPin }) {
    return request('/me/pin/forgot/confirm', {
      method: 'POST',
      token,
      body: { code, newPin },
    });
  },
};
